import { Button, Card, Collection, Flex } from "@aws-amplify/ui-react"
import type {
  OrganizationDto,
  RecipeComponent,
  RecipeDto,
  RecipesAusladegruppen,
} from "@strtmn/sds/types"
import { I18n } from "aws-amplify/utils"
import { useContext } from "react"
import { FaBook, FaPenToSquare, FaXmark } from "react-icons/fa6"
import { Link, useParams } from "react-router-dom"
import config from "../../../config"
import { UserDataContext, UserDataContextHelper } from "../../../contexts"
import { getLinkToOrgRecipe, getLinkToOrgRecipes } from "../../../LinkUtils"
import { UNIT_OF_MASS } from "../../../org-settings"
import { imageHash } from "../-components/ListItem"
import AnimalGroupItem from "../-components/AnimalGroupItem"
import FeedComponentItem from "../-components/FeedComponentItem"
import { getFeedComponentGroupLabel } from "../-components/FeedComponentUtils"

import "../RecipeManagement.css"

export function getTotalFreshMass(org: OrganizationDto, recipe: RecipeDto) {
  let totalFreshMass: number | string = recipe.RecipeComponents.reduce<number>(
    (prev: number, cur) => {
      // Negative values are special error codes. Don't alter them.
      if (prev < 0) {
        return prev
      }
      const fc = UserDataContextHelper.findFeedComponent(
        org,
        cur.feed_component_id
      )
      const tsGehaltPercent = Number(fc.ts_gehalt_percent)
      let curMass = cur.mass_kg
      if (tsGehaltPercent > 0) {
        curMass = cur.mass_kg * (100 / tsGehaltPercent)
      }
      return prev + curMass
    },
    0
  )
  // if (-1 === totalFreshMass) {
  //   return "Konnte Frischmasse nicht berechnen"
  // }
  return totalFreshMass
}

export function getTotalNumberOfAnimals(
  org: OrganizationDto,
  recipe: RecipeDto
) {
  const totalAnimals = recipe.RecipesAusladegruppen.reduce((prev, cur) => {
    const ag = UserDataContextHelper.findAnimalGroup(org, cur.ausladegruppe_id)
    return prev + ag.number_of_animals
  }, 0)
  return totalAnimals
}

interface RecipeAusladegruppenProps {
  org: OrganizationDto
  recipeAusladegruppen: RecipesAusladegruppen[]
  freshMassPerAnimal: number
}
function RecipeAusladegruppen({
  org,
  recipeAusladegruppen,
  freshMassPerAnimal,
}: RecipeAusladegruppenProps) {
  return (
    <Collection
      className="recipe-animal-groups"
      type="list"
      direction="column"
      items={recipeAusladegruppen}
    >
      {(item) => {
        const ag = UserDataContextHelper.findAnimalGroup(
          org,
          item.ausladegruppe_id
        )
        const extraDetails = (
          <div>
            Gesamtgewicht:{" "}
            {(ag.number_of_animals * freshMassPerAnimal).toLocaleString(
              undefined,
              { maximumFractionDigits: 0 }
            )}{" "}
            {UNIT_OF_MASS}
          </div>
        )
        return (
          <AnimalGroupItem
            key={`${item.ausladegruppe_id}-${item.order}`}
            org={org}
            item={ag}
            extraDetails={extraDetails}
            hasViewButton={false}
            hideFavStar={true}
          />
        )
      }}
    </Collection>
  )
}

interface RecipeComponentsListProps {
  org: OrganizationDto
  recipeComponents: RecipeComponent[]
}
function RecipeComponentsList({
  org,
  recipeComponents,
}: RecipeComponentsListProps) {
  return (
    <Collection
      className="recipe-components"
      type="list"
      direction="column"
      items={recipeComponents}
    >
      {(item) => {
        const fc = UserDataContextHelper.findFeedComponent(
          org,
          item.feed_component_id
        )
        const tsGehaltPercent = Number(fc.ts_gehalt_percent)
        let freshMass: string | number = item.mass_kg
        if (tsGehaltPercent > 0) {
          freshMass = item.mass_kg * (100 / tsGehaltPercent)
        }
        return (
          <FeedComponentItem
            key={`${item.feed_component_id}-${item.order}`}
            org={org}
            item={fc}
            details={
              <>
                <div>
                  {`${freshMass.toLocaleString(undefined, { maximumFractionDigits: 3 })} ${UNIT_OF_MASS}`}
                </div>
                <div>
                  {I18n.get("Kategorie")}:{" "}
                  {I18n.get(getFeedComponentGroupLabel(fc.group))}
                </div>
              </>
            }
            hasViewButton={false}
          />
        )
      }}
    </Collection>
  )
}

const NUM_RC_IMAGES = config.images.rc.numImages
const IMAGE_HASH_SEED = config.images.rc.imageHashSeed

export default function Recipe() {
  const params = useParams()

  const { userData } = useContext(UserDataContext)
  const org = UserDataContextHelper.findOrg(params["orgId"]!, userData!)
  const recipe = UserDataContextHelper.findRecipe(org, params["recipeId"]!)

  recipe.RecipeComponents.sort((a, b) => (a.order || 0) - (b.order || 0))
  recipe.RecipesAusladegruppen.sort((a, b) => (a.order || 0) - (b.order || 0))
  const imageIdx = imageHash(IMAGE_HASH_SEED, recipe.id, NUM_RC_IMAGES)
  const imageSrc = `/images/recipes/ration-pixabay-${imageIdx}.jpg`
  const numAnimals = getTotalNumberOfAnimals(org, recipe)
  const freshMassPerAnimal = getTotalFreshMass(org, recipe)
  const columns = [
    {
      title: "Ausladestellen",
      content: (
        <RecipeAusladegruppen
          org={org}
          recipeAusladegruppen={recipe.RecipesAusladegruppen}
          freshMassPerAnimal={freshMassPerAnimal}
        />
      ),
    },
    {
      title: "Komponenten",
      content: (
        <RecipeComponentsList
          org={org}
          recipeComponents={recipe.RecipeComponents}
        />
      ),
    },
  ]
  if (recipe.portions && recipe.portions.length > 0) {
    columns.push({
      title: "Portionen",
      content: (
        <>
          {recipe.portions.map((elem) => {
            return (
              <div>
                {elem.name} | {elem.percentage} %
              </div>
            )
          })}
        </>
      ),
    })
  }
  const cardWidthClass = columns.length === 3 ? "md:!w-1/3" : "md:!w-1/2"
  return (
    <>
      <div className="rm-view-page">
        <div
          className="hdr-img"
          style={{ backgroundImage: `url('${imageSrc}')` }}
        />
        <Link to="..">
          <Button className="button-close has-icon">
            <span role="img" aria-label="Schließen">
              <FaXmark />
            </span>
          </Button>
        </Link>
        <div className="content-overlay">
          <div className="header">
            <div className="info">
              {/** @todo: Make fav star clickable */}
              <h2>
                {recipe.name}
                {/* <FaRegStar />*/}
              </h2>
              <div className="basic-info">
                {numAnimals} Tiere
                <br />
                Gewicht:{" "}
                {freshMassPerAnimal.toLocaleString(undefined, {
                  maximumFractionDigits: 1,
                })}{" "}
                {UNIT_OF_MASS} pro Tier
                <br />
                Gesamtgewicht:{" "}
                {(numAnimals * freshMassPerAnimal).toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}{" "}
                {UNIT_OF_MASS}
              </div>
            </div>
            <Link to={getLinkToOrgRecipe(org, recipe.id, true)}>
              <Button className="button-edit has-icon">
                <span role="img" aria-label="Bearbeiten">
                  <FaPenToSquare />
                </span>
              </Button>
            </Link>
          </div>

          <Collection
            className="details recipe-columns"
            type="list"
            direction="row"
            justifyContent="space-between"
            items={columns}
          >
            {(item) => {
              return (
                <Card
                  className={`!p-2 w-full ${cardWidthClass}`}
                  key={item.title}
                >
                  <div className="font-semibold text-[16px] mb-4">
                    {item.title}
                  </div>
                  <div className="text-[12px]">{item.content}</div>
                </Card>
              )
            }}
          </Collection>
        </div>
      </div>

      {/* <small>* Die Icons "xmark (light)", "pen-to-square (light)" benötigen FontAwesome Pro, kostet 49,- oder 99,- pro Jahr.</small> */}
    </>
  )
}

export const Component = Recipe

export const ErrorComponent = () => {
  const params = useParams()
  const { userData } = useContext(UserDataContext)
  const org = UserDataContextHelper.findOrg(params["orgId"]!, userData!)

  const icon = <FaBook className="w-5 _h-3.5_ relativ text-[#3E9E29]" />
  const title = "Rezepte"
  return (
    <>
      <div className="rm-list-page">
        <Flex className="!h-12 !pb-2 !justify-between !items-center">
          <Flex className="!h-6 !justify-start !items-center gap-3">
            {icon}
            <div className="text-xl font-semibold">{title}</div>
          </Flex>
        </Flex>

        <div className="error mb-4 pl-2">Fehler: Rezept nicht gefunden.</div>

        <Link to={getLinkToOrgRecipes(org)}>
          <Button>Zurück zur Liste</Button>
        </Link>
      </div>
    </>
  )
}
